import { useEffect, useState } from "react";
import { useSignOut } from "@/lib/auth-client";
import { Mail, ShieldAlert, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const AUTO_SIGNOUT_SECONDS = 5;

export function NotAuthorized({
  message,
  allowedDomains,
}: {
  message?: string | null;
  allowedDomains?: string[];
}) {
  const signOut = useSignOut();
  const [secondsLeft, setSecondsLeft] = useState(AUTO_SIGNOUT_SECONDS);

  const domainsList =
    allowedDomains && allowedDomains.length > 0
      ? allowedDomains.map((d) => `@${d}`).join(", ")
      : "@wandaharrisllc.com";

  const headline = "This account is not authorized";
  const body =
    message ??
    `MailCraft is restricted to ${domainsList} email addresses only. ` +
      `Your account will be signed out automatically. ` +
      `Please sign in with a company email or contact your administrator if you believe this is a mistake.`;

  useEffect(() => {
    const tick = setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    const bounce = setTimeout(() => {
      signOut();
    }, AUTO_SIGNOUT_SECONDS * 1000);
    return () => {
      clearInterval(tick);
      clearTimeout(bounce);
    };
  }, [signOut]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="not-authorized-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm px-4"
    >
      <Card className="max-w-md w-full shadow-2xl border-destructive/40">
        <CardContent className="pt-8 pb-6 px-6 flex flex-col items-center text-center gap-4">
          <div className="h-14 w-14 rounded-full bg-destructive/10 flex items-center justify-center">
            <ShieldAlert className="h-7 w-7 text-destructive" />
          </div>
          <div className="space-y-2">
            <h1
              id="not-authorized-title"
              className="text-xl font-semibold tracking-tight"
            >
              {headline}
            </h1>
            <p className="text-sm text-muted-foreground">{body}</p>
            <p
              className="text-xs text-muted-foreground pt-1"
              data-testid="not-authorized-countdown"
            >
              Signing out in {secondsLeft}s…
            </p>
          </div>
          <Button
            variant="default"
            className="w-full mt-2"
            onClick={() => signOut()}
            data-testid="button-not-authorized-signout"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign out now
          </Button>
          <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2">
            <Mail className="h-3.5 w-3.5" />
            <span>MailCraft</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
